import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  OnInit,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { JobDropdown } from '@ups/xplat/api/dto';
import { SearchJobService, SearchJobVPService } from '@ups/xplat/api/services';
import {
  dynamicJobCompanyScopeParams,
  dynamicTypeaheadJobFilter,
  dynamicWithoutCompanyScope,
  withFormFillJobScope,
} from '@ups/xplat/dynamic-render';
import { Store } from '@ngrx/store';
import { UserState } from '@ups/user';
import { take, takeUntil } from 'rxjs';
import { DynamicTypeaheadWebBaseComponent } from '../base/dynamic-typeahead.web-base-component';

const SEARCH_CONTROL_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DynamicTypeaheadJobComponent),
  multi: true,
};

@Component({
  selector: 'ups-dynamic-typeahead-job',
  templateUrl: 'dynamic-typeahead-job.component.html',
  providers: [SEARCH_CONTROL_VALUE_ACCESSOR],
  // WI 35584: template state is marked at each async write (loading, search results, target
  // updates) in the shared dynamic-render bases.
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DynamicTypeaheadJobComponent
  extends DynamicTypeaheadWebBaseComponent
  implements OnInit
{
  /**
   * The facility active-jobs tooltip in the item template. The template read this as an undeclared field, so the
   * tooltip has never rendered; WI 34037 declares it off to keep that (strictTemplates).
   */
  protected readonly showFacilityTooltip = false;
  private store = inject(Store);
  private readonly jobsService = inject(SearchJobService);
  private readonly jobsVPService = inject(SearchJobVPService);

  constructor() {
    super();
    this.queryFilter = dynamicTypeaheadJobFilter;
  }

  ngOnInit() {
    super.ngOnInit();
    this.searchQuery = this.config?.options?.pointToViewpoint
      ? this.jobsVPService.search.bind(this.jobsVPService)
      : this.jobsService.search.bind(this.jobsService);
    // WI 34031: a form-fill job control opts into any-active-job (server-side
    // scope bypass); direct page usages carry no such option. Applied before the
    // WI 34969 company scope, whose updates spread the existing params.
    this.customQueryParams = withFormFillJobScope(
      this.customQueryParams,
      this.config?.options
    );
    // WI 34969: fail closed (no jobs) until the profile says which company is the default.
    const scope = dynamicJobCompanyScopeParams(this.config?.options, undefined);
    if (scope) {
      this.customQueryParams = {
        ...(this.customQueryParams as object),
        ...scope,
      };
      this.store
        .pipe(
          UserState.selectCurrentTruthy(),
          take(1),
          takeUntil(this.destroy$)
        )
        .subscribe((myInfo) => {
          this.customQueryParams = {
            ...(this.customQueryParams as object),
            ...dynamicJobCompanyScopeParams(
              this.config?.options,
              myInfo?.Data?.VPCompanyID
            ),
          };
        });
    }
  }

  /** WI 34969: a saved job is re-found without the company scope, so one outside the default company still shows. */
  override findMatchToPrefill(query: string, complete = true) {
    const scoped = this.customQueryParams;
    this.customQueryParams = dynamicWithoutCompanyScope(scoped);
    try {
      super.findMatchToPrefill(query, complete);
    } finally {
      this.customQueryParams = scoped;
    }
  }

  toggleFavorite(job: JobDropdown, e: PointerEvent) {
    job.IsUserFavorite = !job.IsUserFavorite;
    if (this.config?.options?.pointToViewpoint) {
      this.jobsVPService.addOrRemoveFavoriteJob(job.JobID).then(() => {});
    } else {
      this.jobsService.addOrRemoveFavoriteJob(job.Id).then(() => {});
    }
    e.stopPropagation();
  }
}
